/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ChargeLine, Customer, ProductDeliveryOptions, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl, getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';
import { ICartState } from '@msdyn365-commerce/global-state';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    IPopupProps,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { getCartState } from '@msdyn365-commerce/global-state';
import axios from 'axios';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { vSI_RemoveAllChargesAsync } from '../../../../vsi-add-charges-extension/DataActionExtension.g';
import { GetUnbxdAddToCartProductInput } from '../../../../actions/get-unbxd-add-to-cart-product.action';

export interface IDobbiesPopUpProps extends IPopupProps {
    cart?: ICartState;
    customerInformation?: Customer;
}

export interface IDobbiesPopUpProps extends IComponentProps<{ product: SimpleProduct | undefined; price: ProductPrice | undefined }> {
    dialogStrings?: {
        goToCartText: string;
        continueShoppingText: string;
        headerItemOneText: string;
        headerItemFormatText: string;
        headerMessageText: string;
        freePriceText: string;
        originalPriceText: string;
        currentPriceText: string;
    };
    cart?: ICartState;
    className: string;
    imageSettings?: IImageSettings;
    gridSettings: IGridSettings | undefined;
    productQuantity: number;
    priceComponent: React.ReactNode;
    navigationUrl?: string;
    modalOpen: boolean | undefined;
    telemetryContent?: ITelemetryContent;
    setModalOpen(newValue: boolean): void;
    unbxdCarousel: React.ReactNode[];
}

export const Popup = (popupProps: IDobbiesPopUpProps) => {
    const {
        dialogStrings,
        className,
        gridSettings,
        data: { product, price },
        productQuantity,
        priceComponent,
        navigationUrl,
        modalOpen,
        telemetryContent,
        setModalOpen,
        context
    } = popupProps;
    const closeModal = React.useCallback(() => {
        setModalOpen(false);
        document.body.classList.remove('buybox-open');
    }, [setModalOpen]);
    const OnOpenModal = React.useCallback(() => {
        document.body.classList.add('buybox-open');
    }, [setModalOpen]);

    if (!dialogStrings || !product) {
        return null;
    }
    const productURL = getProductUrlSync(product, popupProps.context.actionContext, undefined);

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.ContinueShopping);
    const continueShoppingAttribute = getTelemetryAttributes(telemetryContent!, payLoad);
    payLoad.contentAction.etext = TelemetryConstant.ViewBagAndCheckout;
    const viewBagAttribute = getTelemetryAttributes(telemetryContent!, payLoad);
    const formatCurrency = (price: number | undefined): string | undefined => {
        return context.cultureFormatter.formatCurrency(price || 0, undefined);
    };
    const fallbackImageUrl = getFallbackImageUrl(
        product.ItemId,
        context.actionContext.requestContext.apiSettings,
        context.request.app?.config?.OmniChannelMedia
    );

    const imageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=315&h=443&m=6', w: 315, h: 443 },
            lg: { q: 'w=315&h=443&m=6', w: 315, h: 443 },
            xl: { q: 'w=315&h=443&m=6', w: 315, h: 443 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    const [basketDeliveryCost, setBasketDeliveryCost] = React.useState(0);
    const [overlayCartState, setOverlayCartState] = React.useState<ICartState | undefined>();
    const [deliveryOptionsState, setDeliveryOptionsState] = React.useState<ProductDeliveryOptions[]>([]);
    const [isCncOnlyCart, setIsCNCOnlyCart] = React.useState<boolean>();

    const removeAllCharges = async (cartState: ICartState) => {
        try {
            if (cartState.cart.Id) {
                await vSI_RemoveAllChargesAsync({ callerContext: context.actionContext }, cartState.cart.Id);
            }
        } catch (e) {
            console.error('Error occured while removing charges', e);
        }
    };

    const addChargeToCart = async (cartState: ICartState, amount: number) => {
        const userToken = context.request.user.token;
        const isAuthenticated = context.request.user.isAuthenticated;
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            OUN: context.request.apiSettings.oun
        };
        if (isAuthenticated) {
            headers['Authorization'] = `id_token ${userToken}`;
        }
        try {
            const res = await axios({
                method: 'POST',
                url: `${context.request.apiSettings.baseUrl}Commerce/VSIAddCharge/VSI_AddCharge`,
                data: {
                    cartId: cartState.cart.Id,
                    moduleTypeValue: 1,
                    chargeCode: 'Shipping',
                    amount: amount
                },
                headers: headers
            });
            await cartState.refreshCart({});
            context.request.cookies.set('default_customer_address_for_delivery_set', 'true');
            // let deliveryCost = 0;
            // res.data.ChargeLines?.forEach((chargeLine: { CalculatedAmount: number }) => {
            //     deliveryCost += chargeLine.CalculatedAmount || 0;
            // });
            res.data.ChargeLines?.reduce((acc: number, c: { CalculatedAmount: number }) => acc + (c.CalculatedAmount || 0), 0);
        } catch (error) {
            console.error('Adding charges error', error);
        }
    };
    const addSTD2ChargesToCart = async (cartState: ICartState) => {
        await addChargeToCart(cartState, context.request.app.config.chargesForSTD2Items);
    };
    const addHDChargesToCart = async (cartState: ICartState) => {
        await addChargeToCart(cartState, context.request.app.config.chargesForHDItems);
    };

    const updateDeliveryCharges = async () => {
        const cartState = overlayCartState || (await getCartState(context.actionContext));
        if (!cartState) return;

        const freeDeliveryThreshold: number = context?.actionContext.requestContext?.app?.config?.freeDeliveryMilestone || 0;
        const subTotalAmount = cartState.cart.SubtotalAmount || 0;

        const deliveryOptions: ProductDeliveryOptions[] = await getDeliveryOptionsAsync(
            { callerContext: context.actionContext, queryResultSettings: {} },
            cartState.cart.CartLines?.map(cl => cl.ProductId).filter((id): id is number => id != null) || []
        );
        setDeliveryOptionsState(deliveryOptions);
        const isOnlyCNCCart = cartState.cart.CartLines?.find(cl => !cl.FulfillmentStoreId) ? false : true;
        const hasSTD2Item = deliveryOptions.some(productOption =>
            productOption.DeliveryOptions?.some(option => {
                const isSTD2Item = option.Code === 'STD-2';
                const isCNCItem = cartState.cart.CartLines?.find(
                    cl => cl.ProductId === productOption.ProductId && cl.FulfillmentStoreId && cl.FulfillmentStoreId !== ''
                );
                return isSTD2Item && !isCNCItem;
            })
        );
        const hasHDItem = deliveryOptions.some(productOption =>
            productOption.DeliveryOptions?.some(option => option.Code === 'HD' || option.Code === 'HD Presale')
        );
        // Check if cart already has STD2 charges, only apply new charges if charges don't exists
        const hasSTD2Charges = cartState.cart.ChargeLines?.find(
            charge => charge.IsShipping && charge.CalculatedAmount === context.request.app.config.chargesForSTD2Items
        );
        // Check if cart already has HD charges, only apply new charges if charges don't exists
        const hasHDCharges = cartState.cart.ChargeLines?.find(
            charge => charge.IsShipping && charge.CalculatedAmount === context.app.config.chargesForHDItems
        );

        if (!hasHDItem && hasSTD2Item && !hasSTD2Charges && !isOnlyCNCCart) {
            await removeAllCharges(cartState);
            await addSTD2ChargesToCart(cartState);
        }

        if (context.request.app.config.addChargesForHDItems && hasHDItem && !hasHDCharges) {
            await removeAllCharges(cartState);
            await addHDChargesToCart(cartState);
        }

        if (!hasHDItem && !hasSTD2Item) {
            // Check if cart already has STD charges, only apply new charges if charges don't exists
            const hasSTDCharges = cartState.cart.ChargeLines?.find(
                charge => charge.IsShipping && charge.CalculatedAmount === context.app.config.chargesForHomeDeliveryItems
            );

            const cncItems = cartState?.cart.CartLines?.filter(
                line => line.DeliveryMode === popupProps.context.request.channel?.PickupDeliveryModeCode
            );
            const isCNCCart = cncItems?.length === cartState.cart.CartLines?.length;
            setIsCNCOnlyCart(isCNCCart);
            if (subTotalAmount >= freeDeliveryThreshold || isCNCCart) {
                await removeAllCharges(cartState);
                await addChargeToCart(cartState, 0);
            } else if (!hasSTDCharges) {
                await removeAllCharges(cartState);
                await addChargeToCart(cartState, context.request.app.config.chargesForHomeDeliveryItems);
            }
        }
        // update local delivery charges state with updated cart charges
        const updatedCartState = await getCartState(context.actionContext);
        const deliveryCharges = updatedCartState.cart.ChargeLines?.reduce((prevValue, charge: ChargeLine) => {
            return prevValue + (charge.CalculatedAmount || 0);
        }, 0);
        setBasketDeliveryCost(deliveryCharges || 0);
    };

    React.useEffect(() => {
        if (modalOpen) {
            getCartState(context.actionContext)
                .then(res => {
                    setOverlayCartState(res);
                    updateDeliveryCharges();
                })
                .catch(e => {
                    console.error(e);
                });
        } else {
            context.actionContext.update(new GetUnbxdAddToCartProductInput(), { product: null });
        }
    }, [modalOpen]);

    const quantity = productQuantity !== undefined ? productQuantity : 1;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
    const shouldDisplayUnitOfMeasure =
        context.app.config?.unitOfMeasureDisplayType && context.app.config.unitOfMeasureDisplayType !== 'none';
    const reducedDimensions: string = product.Dimensions
        ? product.Dimensions.reduce<string>((str, productDimension) => {
              if (productDimension.DimensionValue && productDimension.DimensionValue.Value) {
                  if (str) {
                      return `${str}, ${productDimension.DimensionValue.Value}`;
                  }

                  return `${productDimension.DimensionValue.Value}`;
              }

              return str;
          }, '')
        : '';

    const homeDeliveryProducts = overlayCartState?.cart?.CartLines?.filter(cartLine => cartLine?.FulfillmentStoreId === '') || [];
    const cncProducts = overlayCartState?.cart?.CartLines?.filter(cartLine => cartLine?.FulfillmentStoreId !== '') || [];
    let homeDelQty = 0;
    homeDeliveryProducts.forEach(product => {
        homeDelQty += product.Quantity || 0;
    });
    let cncQty = 0;
    cncProducts.forEach(product => {
        cncQty += product.Quantity || 0;
    });
    const totalQuantity = homeDelQty + cncQty;

    const totalAmount = overlayCartState?.cart?.TotalAmount;

    const freeDelivery: number = context?.actionContext.requestContext?.app?.config?.freeDeliveryMilestone || 0;
    const subTotalAmount = overlayCartState?.cart.SubtotalAmount!;
    let spendX: number | Boolean = freeDelivery - subTotalAmount;

    let numWithZeroes;
    if (spendX < 0) {
        spendX = false;
    } else {
        numWithZeroes = parseFloat(spendX.toString()).toFixed(2);
    }
    const isHDItem = deliveryOptionsState.some(productOption =>
        productOption.DeliveryOptions?.some(option => option.Code === 'HD' || option.Code === 'HD Presale')
    );
    const isSTD2Item = deliveryOptionsState.some(productOption => productOption.DeliveryOptions?.some(option => option.Code === 'STD-2'));
    const cartLines = overlayCartState?.cart?.CartLines || [];
    const deliveryModes = cartLines.map(line => line.DeliveryMode?.trim()).filter(Boolean);
    const LARGE_DELIVERY_MODES = ['HD', 'HD Presale'];
    const IGNORED_MODES = ['Clickcol', 'Common MOD'];
    const equalsIgnoreCase = (a: string, b: string | undefined) => a?.toLowerCase() === b?.toLowerCase();
    const relevantModes = deliveryModes.filter(mode => !IGNORED_MODES.some(ignore => equalsIgnoreCase(ignore, mode)));
    const hasHDItemCart = relevantModes.some(mode => LARGE_DELIVERY_MODES.some(large => equalsIgnoreCase(large, mode)));
    const hasSTD2ItemCart = relevantModes.some(mode => mode === 'STD-2');
    const allHDItems = hasHDItemCart && relevantModes.every(mode => LARGE_DELIVERY_MODES.some(large => equalsIgnoreCase(large, mode)));
    const isMixedCart = (hasHDItemCart || hasSTD2ItemCart) && !allHDItems && relevantModes.length > 1;
    const showVariantKitConfig = context?.actionContext.requestContext?.app?.config?.showVariantsKitConfig || false;
    const HDDeliveryCharges = basketDeliveryCost !== undefined && popupProps?.context.cultureFormatter.formatCurrency(basketDeliveryCost);
    let deliveryMessage: string | null = null;
    const uniqueModes = [...new Set(relevantModes?.map(m => m?.toLowerCase()))];
    const hasHD = uniqueModes.some(mode => LARGE_DELIVERY_MODES.some(large => equalsIgnoreCase(large, mode)));
    const hasSTD2 = uniqueModes.some((mode: any) => equalsIgnoreCase(mode, 'STD-2'));
    const isOnlyStdAndPf = uniqueModes.every((mode: any) => equalsIgnoreCase(mode, 'STD') || equalsIgnoreCase(mode, 'PF'));
    const isMixed = uniqueModes.length > 1 && !isOnlyStdAndPf;
    if (isMixed) {
        deliveryMessage = 'Mixed delivery methods.';
    } else if (hasHD) {
        deliveryMessage = 'XL item delivery.';
    } else if (hasSTD2) {
        deliveryMessage = 'Large item delivery.';
    }

    return (
        <Modal isOpen={modalOpen} onClosed={closeModal} onOpened={OnOpenModal} className={`${className}__dialog`} toggle={closeModal}>
            <ModalHeader toggle={closeModal} className={`${className}__dialog__header`}>
                <span>Added to basket</span>
            </ModalHeader>
            <ModalBody className={`${className}__dialog__body`}>
                <div className={`${className}__dialog__body-upper`}>
                    <a href={productURL} className='popup-image'>
                        <Image
                            requestContext={context.actionContext.requestContext}
                            className={`${className}__dialog__image`}
                            src={product.PrimaryImageUrl || ''}
                            fallBackSrc={fallbackImageUrl}
                            altText={product.Name}
                            gridSettings={gridSettings || {}}
                            imageSettings={imageSettings}
                            loadFailureBehavior='empty'
                        />
                    </a>
                    <div className={`${className}__dialog__product-details`}>
                        <a href={productURL} className={`${className}__dialog__product-title`}>
                            {product.Name}
                        </a>
                        {showVariantKitConfig && reducedDimensions !== '' ? (
                            <div className={`${className}__dialog__product-variants`}>{reducedDimensions}</div>
                        ) : (
                            ''
                        )}

                        <p className={`${className}__dialog__quantity`}>
                            Quantity: {quantity}
                            <span>{formatCurrency(price?.CustomerContextualPrice)} each</span>
                        </p>

                        {price ? <div className={`${className}__dialog__product-price`}>{priceComponent}</div> : ''}
                        {product.DefaultUnitOfMeasure && shouldDisplayUnitOfMeasure ? (
                            <div className={`${className}__dialog__product-unitOfMeasure`}>
                                <span>{product.DefaultUnitOfMeasure}</span>
                            </div>
                        ) : (
                            ''
                        )}
                    </div>
                </div>
                <div className={`${className}__dialog__body-price-detail`}>
                    <div className='delivery-cost'>
                        {homeDelQty > 0 && (
                            <p>
                                <span>
                                    Basket delivery cost ({homeDelQty} {homeDelQty > 1 ? 'items' : 'item'})
                                </span>
                                {/* {isHDItem ? `£${basketDeliveryCost}` : !spendX ? 'Free' : `£${basketDeliveryCost}`} */}
                                {basketDeliveryCost === 0
                                    ? 'Free'
                                    : isHDItem || isSTD2Item
                                    ? `${HDDeliveryCharges}`
                                    : !spendX
                                    ? 'Free'
                                    : `£${basketDeliveryCost}`}
                            </p>
                        )}

                        {spendX && !isCncOnlyCart && !isHDItem && !isSTD2Item && !isMixedCart ? (
                            <p className='delivery-cost-text'>Spend £{numWithZeroes} more for free delivery. Eligible items only.</p>
                        ) : (
                            <></>
                        )}
                        {!spendX && !isHDItem && !hasSTD2ItemCart && !isCncOnlyCart ? (
                            <p className='delivery-cost-text'>You have qualified for free delivery.</p>
                        ) : (
                            <></>
                        )}
                        {/* {(hasHDItemCart || allHDItems) && <p className='delivery-cost-text'>XL item delivery.</p>}
                        {isMixedCart && <p className='delivery-cost-text'>Mixed delivery methods.</p>}
                        {hasSTD2ItemCart && <p className='delivery-cost-text'>Large item delivery.</p>} */}

                        {deliveryMessage && <p className='delivery-cost-text'>{deliveryMessage}</p>}
                    </div>
                    {cncQty > 0 && (
                        <div className='cnc-items'>
                            <p>
                                <span>
                                    Click & Collect ({cncQty} {cncQty > 1 ? 'items' : 'item'})
                                </span>{' '}
                                Free
                            </p>
                            <p className='cnc-items-text'>Confirmed store pick-up and further information in basket.</p>
                        </div>
                    )}

                    <p className='basket-total'>
                        <span>
                            <b>Basket total</b> ({totalQuantity} {totalQuantity > 1 ? 'items' : 'item'})
                        </span>
                        {formatCurrency(totalAmount)}
                    </p>
                </div>
            </ModalBody>
            <ModalFooter className={`${className}__dialog__footer`}>
                <Button
                    className={`${className}__dialog__go-to-cart`}
                    onClick={() => {
                        if (navigationUrl) {
                            window.location.href = navigationUrl;
                        }
                    }}
                    aria-label={context.actionContext.requestContext.user.isAuthenticated ? 'View basket' : 'View basket & checkout'}
                    {...viewBagAttribute}
                >
                    {context.actionContext.requestContext.user.isAuthenticated ? 'View basket' : 'View basket & checkout'}
                </Button>
                {context.actionContext.requestContext.user.isAuthenticated ? (
                    <Button
                        className={`${className}__dialog__checkout`}
                        onClick={() => {
                            const checkoutUrl = `${context.actionContext.requestContext.url.requestUrl.origin}/checkout`;
                            window.location.href = checkoutUrl;
                        }}
                        aria-label='Checkout'
                    >
                        Checkout
                    </Button>
                ) : (
                    ''
                )}

                <Button
                    className={`${className}__dialog__back-to-shopping ${
                        context.actionContext.requestContext.user.isAuthenticated ? 'sign-in' : 'not-sign-in'
                    }`}
                    onClick={closeModal}
                    aria-label={dialogStrings.continueShoppingText}
                    {...continueShoppingAttribute}
                >
                    {dialogStrings.continueShoppingText}
                </Button>

                {popupProps.unbxdCarousel && <div className='unbxd-recommendations pdp'>{popupProps.unbxdCarousel}</div>}
            </ModalFooter>
        </Modal>
    );
};

export default Popup;
